'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Smartphone,
  MapPin,
  Route,
  Camera,
  Bell,
  CreditCard,
  Users,
  Shield,
  Fingerprint,
  Wifi,
  WifiOff,
  Download,
  ArrowRight,
  Check,
  Zap,
  BarChart3,
  Globe,
  Palette,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

/* ── Data ──────────────────────────────────────────────────── */

const features = [
  {
    icon: MapPin,
    title: 'Live Fleet Map',
    description: 'Full Google Maps integration with real-time vehicle positions via SSE streaming. Smart clustering handles fleets of any size.',
    highlights: ['Sub-second position updates', 'Tap-to-view vehicle details', 'Street-level context'],
  },
  {
    icon: Route,
    title: 'Trip History & Playback',
    description: 'Review complete trip timelines with route paths, stop markers, speed profiles, and trigger events. Replay any trip visually.',
    highlights: ['Visual trip replay', 'Stop & idle analysis', 'Speed & fuel graphs'],
  },
  {
    icon: Camera,
    title: 'Camera & Video',
    description: 'Stream live HLS video directly on your phone. View snapshots, review ADAS/DMS clips, and access the full video timeline.',
    highlights: ['Quad-camera live view', 'Event clip playback', 'Snapshot gallery'],
  },
  {
    icon: Bell,
    title: 'Push Notifications',
    description: 'Receive real-time push alerts for trigger events, geofence breaches, safety incidents, and system notifications via FCM.',
    highlights: ['Firebase Cloud Messaging', 'Per-alert sound & vibration', 'Notification center'],
  },
  {
    icon: CreditCard,
    title: 'Billing & Invoices',
    description: 'View subscription status, download invoices, process payments, and track billing history from the mobile interface.',
    highlights: ['Subscription management', 'Invoice download', 'Payment history'],
  },
  {
    icon: Users,
    title: 'Admin Controls',
    description: 'Fleet managers can manage users, vehicles, fleets, organizations, and support tickets on the go.',
    highlights: ['User management', 'Fleet organization', 'Ticket system'],
  },
  {
    icon: Shield,
    title: 'Driver Safety Scores',
    description: 'View real-time driver scorecards, harsh event history, compliance status, and UBI risk scores.',
    highlights: ['Safety scorecards', 'Event timeline', 'Compliance tracking'],
  },
  {
    icon: BarChart3,
    title: 'Reports & Analytics',
    description: 'Access the full report catalog, view dashboards, and export reports directly from your mobile device.',
    highlights: ['100+ report types', 'Interactive charts', 'PDF/CSV export'],
  },
];

const techSpecs = [
  { label: 'Framework', value: 'Flutter 3.16+' },
  { label: 'Language', value: 'Dart 3.2+' },
  { label: 'State Management', value: 'flutter_bloc' },
  { label: 'Navigation', value: 'go_router (deep links)' },
  { label: 'HTTP Client', value: 'Dio (JWT + tenant interceptors)' },
  { label: 'Maps', value: 'google_maps_flutter + clustering' },
  { label: 'Push', value: 'Firebase Messaging' },
  { label: 'Auth', value: 'JWT + biometrics' },
  { label: 'DI', value: 'get_it + injectable' },
  { label: 'Platforms', value: 'iOS 14+ / Android 8+' },
];

const screens = [
  { label: 'Live Map', desc: 'Real-time fleet view with clustering' },
  { label: 'Vehicle Detail', desc: 'Status, telemetry, trips, and camera' },
  { label: 'Trip Playback', desc: 'Animated route replay with events' },
  { label: 'Notifications', desc: 'Alert feed with actions' },
  { label: 'Driver Score', desc: 'Safety metrics and trend charts' },
  { label: 'Reports', desc: 'Catalog and interactive viewer' },
];

export default function MobileAppPage() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-blue-50 py-20 lg:py-28">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-sky-200/20 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 h-[400px] w-[400px] rounded-full bg-blue-200/15 blur-[100px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Text */}
            <div className="space-y-6">
              <Badge variant="accent" className="bg-sky-50 text-sky-700 border-sky-200">
                <Smartphone className="h-3 w-3" /> Mobile App
              </Badge>
              <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                Fleet intelligence{' '}
                <span className="bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
                  in your pocket.
                </span>
              </h1>
              <p className="max-w-lg text-lg leading-relaxed text-slate-300">
                The GlonexTrack mobile app brings live tracking, camera feeds, driver scores, billing,
                and admin controls to iOS and Android. Built with Flutter for native performance.
              </p>
              <div className="max-w-xl">
                <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                  <Image
                    src={PAGE_HERO_IMAGES.mobileApp.src}
                    alt={PAGE_HERO_IMAGES.mobileApp.alt}
                    width={1600}
                    height={900}
                    className="h-auto w-full rounded-2xl object-cover"
                    priority
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button variant="accent" size="lg" className="rounded-full bg-sky-600 hover:bg-sky-700 shadow-lg shadow-sky-500/20">
                  <Download className="h-4 w-4" /> Download for iOS
                </Button>
                <Button variant="accent" size="lg" className="rounded-full bg-slate-900 hover:bg-slate-800 shadow-lg">
                  <Download className="h-4 w-4" /> Download for Android
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-300">
                <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-sky-500" /> Native performance</span>
                <span className="flex items-center gap-1.5"><Fingerprint className="h-3.5 w-3.5 text-sky-500" /> Biometric auth</span>
                <span className="flex items-center gap-1.5"><WifiOff className="h-3.5 w-3.5 text-sky-500" /> Offline capable</span>
              </div>
            </div>

            {/* Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                {/* Phone frame */}
                <div className="w-[280px] rounded-[2.5rem] border-[8px] border-slate-800 bg-slate-900 p-1 shadow-2xl shadow-slate-900/30">
                  {/* Notch */}
                  <div className="absolute left-1/2 top-0 -translate-x-1/2 h-6 w-28 rounded-b-2xl bg-slate-800 z-10" />
                  {/* Screen */}
                  <div className="rounded-[2rem] bg-white overflow-hidden">
                    {/* Status bar */}
                    <div className="flex items-center justify-between bg-sky-600 px-5 pt-8 pb-3">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-white/80" />
                        <span className="text-xs font-semibold text-white">GlonexTrack</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Wifi className="h-3 w-3 text-white/60" />
                        <Bell className="h-3 w-3 text-white/60" />
                      </div>
                    </div>
                    {/* Map mock */}
                    <div className="relative h-44 bg-gradient-to-b from-sky-50 to-emerald-50">
                      <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle, #0ea5e9 1px, transparent 1px)', backgroundSize: '20px 20px'}} />
                      {[{t:'25%',l:'30%'},{t:'45%',l:'60%'},{t:'35%',l:'50%'},{t:'60%',l:'25%'},{t:'20%',l:'70%'}].map((p,i) => (
                        <motion.div
                          key={i}
                          className="absolute h-2.5 w-2.5 rounded-full bg-sky-500 shadow-sm shadow-sky-500/30 border border-white"
                          style={{ top: p.t, left: p.l }}
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, delay: i * 0.4, repeat: Infinity }}
                        />
                      ))}
                      <div className="absolute bottom-2 left-2 rounded-lg bg-white/90 backdrop-blur-sm px-2 py-1 shadow-sm">
                        <p className="text-[8px] font-semibold text-slate-700">142 vehicles online</p>
                      </div>
                    </div>
                    {/* Quick stats */}
                    <div className="grid grid-cols-3 gap-1.5 px-3 py-2">
                      {[{l:'Moving',v:'98',c:'text-emerald-600'},{l:'Idle',v:'32',c:'text-amber-600'},{l:'Alerts',v:'3',c:'text-red-600'}].map(s => (
                        <div key={s.l} className="rounded-lg bg-slate-50 p-1.5 text-center">
                          <p className={`text-sm font-bold ${s.c}`}>{s.v}</p>
                          <p className="text-[7px] text-slate-300">{s.l}</p>
                        </div>
                      ))}
                    </div>
                    {/* Bottom nav */}
                    <div className="flex items-center justify-around border-t border-slate-100 py-2">
                      {[MapPin, Route, Camera, Bell, Users].map((Icon, i) => (
                        <div key={i} className={`rounded-lg p-1.5 ${i === 0 ? 'bg-sky-50' : ''}`}>
                          <Icon className={`h-4 w-4 ${i === 0 ? 'text-sky-600' : 'text-slate-300'}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Reflection glow */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-8 w-48 rounded-full bg-sky-400/10 blur-xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── App Screens ──────────────────────────────── */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-slate-900">Key Screens</h2>
            <p className="mt-3 text-base text-slate-300">Every critical fleet operation is one tap away.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {screens.map((screen, i) => (
              <motion.div
                key={screen.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-4 rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{screen.label}</h3>
                  <p className="text-sm text-slate-300">{screen.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ────────────────────────────── */}
      <section className="bg-slate-50/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <Badge variant="accent" className="mb-4 bg-sky-50 text-sky-700 border-sky-200">Full Feature Set</Badge>
            <h2 className="text-3xl font-bold text-slate-900">The complete platform, mobile-native</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {features.map((feat, i) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200/60 bg-white p-8 shadow-sm"
              >
                <div className="mb-4 inline-flex rounded-xl bg-sky-50 p-3 text-sky-600">
                  <feat.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{feat.title}</h3>
                <p className="mt-2 text-sm text-slate-300">{feat.description}</p>
                <ul className="mt-4 space-y-1.5">
                  {feat.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="h-3.5 w-3.5 shrink-0 text-sky-500" /> {h}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── White-Label on Mobile ────────────────────── */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Badge variant="accent" className="mb-4 bg-indigo-50 text-indigo-700 border-indigo-200">
                <Palette className="h-3 w-3" /> White-Label Mobile
              </Badge>
              <h2 className="text-3xl font-bold text-slate-900">Your brand on every phone</h2>
              <p className="mt-3 text-base text-slate-300">
                The mobile app loads your organization&apos;s branding at runtime. One universal build,
                infinite brands. Your clients see your logo, your colors, and your name -- never GlonexTrack.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Logo and app icon loaded from your tenant settings',
                  'Brand colors applied to navigation, buttons, and accents',
                  'Custom splash screen with your branding',
                  'Tenant-specific push notification channels',
                  'No separate app builds per client',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" /> {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Link href="/white-label">
                  <Button variant="outline" className="rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50">
                    Learn about white-labeling <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            {/* Visual */}
            <div className="flex justify-center gap-4">
              {[{brand: 'FleetCo', color: 'bg-emerald-500'}, {brand: 'SafeDrive', color: 'bg-violet-500'}].map((b) => (
                <motion.div
                  key={b.brand}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="w-36 rounded-2xl border border-slate-200 bg-white p-2 shadow-md"
                >
                  <div className={`rounded-t-xl ${b.color} px-3 py-2`}>
                    <p className="text-[9px] font-bold text-white">{b.brand}</p>
                  </div>
                  <div className="space-y-1.5 p-2">
                    <div className="h-16 rounded-lg bg-slate-50" />
                    <div className="h-2 w-16 rounded bg-slate-100" />
                    <div className="h-2 w-12 rounded bg-slate-100" />
                  </div>
                  <div className="flex justify-around border-t border-slate-100 py-1.5">
                    {[MapPin, Bell, Users].map((Icon, i) => (
                      <Icon key={i} className="h-3 w-3 text-slate-300" />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Tech Specs ───────────────────────────────── */}
      <section className="bg-slate-50/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-slate-900">Technical Specifications</h2>
            <p className="mt-3 text-base text-slate-300">Built for performance, security, and cross-platform consistency.</p>
          </div>
          <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200/60 bg-white overflow-hidden shadow-sm">
            {techSpecs.map((spec, i) => (
              <div key={spec.label} className={`flex items-center justify-between px-6 py-3 ${i < techSpecs.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <span className="text-sm font-medium text-slate-300">{spec.label}</span>
                <span className="text-sm font-semibold text-slate-900">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-sky-600 to-blue-600 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white">Get the GlonexTrack mobile app</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sky-100">
            Available on iOS and Android. Download now or sign up to get access for your entire team.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button size="lg" className="rounded-full bg-white text-sky-700 hover:bg-sky-50">
              <Download className="h-4 w-4" /> App Store
            </Button>
            <Button size="lg" className="rounded-full bg-slate-900 text-white hover:bg-slate-800">
              <Download className="h-4 w-4" /> Google Play
            </Button>
            <Link href="/signup">
              <Button size="lg" variant="ghost" className="rounded-full border border-white/30 text-white hover:bg-white/10">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
